import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { FiltersMessages } from './filters-i18n-provider';
import { FiltersI18nProvider } from './filters-i18n-provider';
import type { FiltersTheme } from './filters-theme-provider';
import { FiltersThemeProvider } from './filters-theme-provider';
import type { FilterValueSetState } from './types';

type FiltersScheme = Record<string, unknown | unknown[]>;

type SetFilterValue = <T = unknown>(
  id: string,
  callback: FilterValueSetState<T>
) => void;
type GetFilterValue = <T = unknown>(id: string) => T;

type WidgetFiltersContextI<T = FiltersScheme> = {
  filters: T;
  appliedFilters: T;
  getFilterValue: GetFilterValue;
  getAppliedFilterValue: GetFilterValue;
  setFilterValue: SetFilterValue;
  setAppliedFilterValue: SetFilterValue;
  applySelectedFilters: () => void;
  discardSelectedFilters: () => void;
  resetFilters: () => void;
  clearSelectedFilters: () => void;
  isFilterSelected: (id?: string) => boolean | number;
  isFilterApplied: (id?: string) => boolean | number;
  filtersSelectedCount: number;
  filtersAppliedCount: number;
};

export const WidgetFiltersContext = createContext<WidgetFiltersContextI | null>(
  null
);

type WidgetFiltersProviderProps = PropsWithChildren<{
  theme: FiltersTheme;
  initialFilters?: FiltersScheme;
  i18n?: FiltersMessages;
}>;

export function WidgetFiltersProvider({
  children,
  initialFilters = {},
  theme,
  i18n
}: WidgetFiltersProviderProps) {
  const [filters, setFilters] = useState<FiltersScheme>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FiltersScheme>(initialFilters);

  const applySelectedFilters = useCallback(() => {
    setAppliedFilters(filters);
  }, [filters]);

  const discardSelectedFilters = useCallback(() => {
    setFilters(appliedFilters);
  }, [appliedFilters]);

  const getAppliedFilterValue = useCallback(
    function getAppliedFilterValue<T = unknown>(id: string) {
      return appliedFilters[id] as T;
    },
    [appliedFilters]
  );

  const getFilterValue = useCallback(
    function getFilterValue<T = unknown>(id: string) {
      return filters[id] as T;
    },
    [filters]
  );

  const isFilterSelected = useCallback(
    (id?: string) => {
      if (!id) {
        return Object.keys(filters).some(isFilterSelected);
      }

      const value = filters[id];
      if (Array.isArray(value)) {
        return value.length;
      }

      return value !== undefined && value !== null && value !== '';
    },
    [filters]
  );

  const isFilterApplied = useCallback(
    (id?: string) => {
      if (!id) {
        return Object.keys(appliedFilters).some(isFilterApplied);
      }

      const value = appliedFilters[id];
      if (Array.isArray(value)) {
        return value.length;
      }

      return value !== undefined && value !== null && value !== '';
    },
    [appliedFilters]
  );

  const filtersSelectedCount = useMemo(
    () =>
      Object.keys(filters).reduce(
        (acc, id) => acc + Number(isFilterSelected(id)),
        0
      ),
    [filters, isFilterSelected]
  );

  const filtersAppliedCount = useMemo(
    () =>
      Object.keys(appliedFilters).reduce(
        (acc, id) => acc + Number(isFilterApplied(id)),
        0
      ),
    [appliedFilters, isFilterApplied]
  );

  const setFilterValue = useCallback(function setFilterValue<T = unknown>(
    id: string,
    callback: (prev: T) => T
  ) {
    return setFilters((prev) => ({ ...prev, [id]: callback(prev[id] as T) }));
  }, []);

  const setAppliedFilterValue = useCallback(function setFilterValue<
    T = unknown
  >(id: string, callback: (prev: T) => T) {
    return setAppliedFilters((prev) => ({
      ...prev,
      [id]: callback(prev[id] as T)
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
    setAppliedFilters({});
  }, []);

  const clearSelectedFilters = useCallback(() => {
    setFilters({});
  }, []);

  return (
    <WidgetFiltersContext.Provider
      value={{
        filters,
        appliedFilters,
        filtersSelectedCount,
        filtersAppliedCount,
        getFilterValue,
        getAppliedFilterValue,
        setFilterValue,
        setAppliedFilterValue,
        resetFilters,
        clearSelectedFilters,
        isFilterSelected,
        isFilterApplied,
        applySelectedFilters,
        discardSelectedFilters
      }}
    >
      <FiltersI18nProvider messages={i18n}>
        <FiltersThemeProvider theme={theme}>{children}</FiltersThemeProvider>
      </FiltersI18nProvider>
    </WidgetFiltersContext.Provider>
  );
}

export function useFilters<T extends FiltersScheme>() {
  const context = useContext(WidgetFiltersContext);

  if (context === null) {
    throw new Error(
      `useFiltersContext should be used within WidgetFiltersContext`
    );
  }

  return context as WidgetFiltersContextI<T>;
}
